import { useEffect, useState } from "react";
import { Plus, Trash2, AlertCircle, RefreshCw } from "lucide-react";
import { addBanner, deleteBanner, getAllBanner, uploadFile } from "../service/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";

const Banner = () => {
    const [addModal, setAddModal] = useState(false);
    const [bannerImage, setBannerImage] = useState<string[]>([]);
    const [errors, setErrors] = useState<string | null>(null);
    const [banners, setBanners] = useState([]);
    const [deleteModal, setDeleteModal] = useState(false);
    const [bannerId, setBannerId] = useState<any>();
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        getBanners();
    }, []);

    const getBanners = async () => {
        setLoading(true);
        try {
            let res = await getAllBanner();
            setBanners(res.data.data);
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = async (e: any) => {
        const files = e.target.files;
        if (!files.length) return;

        const formData = new FormData();
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            formData.append('file', file, file.name);
        }
        try {
            const response = await uploadFile(formData);
            setBannerImage(response.data.files);
            setErrors(null);
        } catch (error) {

            toast.error("Image upload failed");
        }
    };

    const handleSubmit = async () => {
        if (bannerImage?.length === 0) {
            setErrors("Please upload at least one image before submitting.");
            return;
        }
        try {
            let body = bannerImage?.map(url => ({ imageUrl: url }));
            await addBanner(body);
            toast.success("Banner added successfully");
            setErrors(null);
            setBannerImage([]);
            setAddModal(false);
            getBanners();
        } catch (err) {
            toast.error("Failed to add banner");
        }
    };

    const handleDelete = (data: any) => {
        setDeleteModal(true);
        setBannerId(data.id);
    };

    const deleteBannerById = async () => {
        try {
            await deleteBanner({ id: bannerId });
            toast.success("Banner deleted");
            setDeleteModal(false);
            getBanners();
        } catch (err) {
            toast.error("Failed to delete banner");
        }
    };

    return (
        <div className="w-full max-w-6xl mx-auto pb-8 space-y-6">
            {/* Header */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-foreground">Banners</h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Home carousel images shown in the app. Upload one or more assets at once.
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => getBanners()}>
                        <RefreshCw className="h-4 w-4" />
                    </Button>
                    <Button size="sm" onClick={() => setAddModal(true)}>
                        <Plus className="h-4 w-4 mr-1" /> Add banner
                    </Button>
                </div>
            </div>

            {/* Table card */}
            <Card className="overflow-hidden">
                {loading ? (
                    <div className="flex min-h-[200px] items-center justify-center">
                        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-12">#</TableHead>
                                    <TableHead>Preview</TableHead>
                                    <TableHead className="text-right w-24">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {banners.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={3} className="text-center text-muted-foreground py-14">
                                            No banners yet.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    banners.map((item: any, index: number) => (
                                        <TableRow key={item.id}>
                                            <TableCell className="text-muted-foreground">{index + 1}</TableCell>
                                            <TableCell>
                                                <img
                                                    src={item.imageUrl}
                                                    alt=""
                                                    className="h-16 w-40 object-cover rounded-lg border"
                                                    onError={(e) => { e.currentTarget.src = "/wood-blog-placeholder.jpg"; }}
                                                />
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8 text-destructive hover:text-destructive"
                                                    onClick={() => handleDelete(item)}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </Card>

            {/* Add banner dialog */}
            <Dialog open={addModal} onOpenChange={setAddModal}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Add banner</DialogTitle>
                        <DialogDescription>Upload one or more banner images for the home carousel.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div className="space-y-1.5">
                            <Label>Images</Label>
                            <input
                                type="file"
                                multiple
                                onChange={handleFileChange}
                                className="block w-full text-sm text-muted-foreground file:mr-4 file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-primary-foreground hover:file:bg-primary/90"
                            />
                            {errors && <p className="text-destructive text-xs">{errors}</p>}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setAddModal(false)}>Cancel</Button>
                        <Button onClick={handleSubmit}>Upload</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete confirmation dialog */}
            <Dialog open={deleteModal} onOpenChange={setDeleteModal}>
                <DialogContent className="max-w-sm">
                    <DialogHeader className="items-center text-center">
                        <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-2" />
                        <DialogTitle>Delete this banner?</DialogTitle>
                        <DialogDescription>This cannot be undone.</DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="flex-row justify-center gap-2 sm:justify-center">
                        <Button variant="outline" onClick={() => setDeleteModal(false)}>Cancel</Button>
                        <Button variant="destructive" onClick={deleteBannerById}>Delete</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default Banner;
